import { CSSProperties, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { IconX } from '@tabler/icons-react'
import classNames from 'classnames'
import { ProductCarouselSlide } from 'shared-types'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { convertProductImageUrl, getImageSrc } from '~/helpers'
import { usePage } from '~/hooks'
import { Gallery } from './Gallery'
import { hasVisibleThumbnails } from './GalleryThumbnails'
import styles from './ProductDashboardGalleryModal.module.scss'
import { useLargestImageAspectRatio } from './useLargestImageAspectRatio'

export interface ProductDashboardGalleryModalProps {
  slides: ProductCarouselSlide[]
  selectedIndex: number
  onSelect: (index: number) => void
  isOpen: boolean
  onClose: () => void
}

export const ProductDashboardGalleryModal = ({
  slides,
  selectedIndex,
  onSelect,
  isOpen,
  onClose,
}: ProductDashboardGalleryModalProps) => {
  const { siteCode } = usePage()
  const mouseDownOnOverlayRef = useRef(false)

  // The dialog takes the shape of the largest image in the thumbnail strip,
  // which only shows image slides. Measured from mount rather than from open,
  // so the ratio is normally known before the dialog is first shown.
  const imageSources = useMemo(() => {
    return slides
      .filter((slide) => {
        return slide.type === 'image' && !!slide.src
      })
      .map((slide) => {
        return getImageSrc(convertProductImageUrl(slide.src, siteCode))
      })
      .filter((src): src is string => {
        return !!src
      })
  }, [slides, siteCode])
  const dialogAspectRatio = useLargestImageAspectRatio(imageSources)
  // Mirrors the Gallery's own check, so the dialog only reserves a row for
  // the thumbnail strip when the strip is actually rendered.
  const showThumbnails = hasVisibleThumbnails(slides, true)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div
      className={styles.overlay}
      role='dialog'
      aria-modal='true'
      onMouseDown={(event) => {
        mouseDownOnOverlayRef.current = event.target === event.currentTarget
      }}
      onClick={(event) => {
        if (
          event.target === event.currentTarget &&
          mouseDownOnOverlayRef.current
        ) {
          onClose()
        }
        mouseDownOnOverlayRef.current = false
      }}>
      <div
        className={classNames(styles.dialog, {
          [styles.withThumbnails]: showThumbnails,
        })}
        style={
          {
            '--pdp-gallery-modal-aspect-ratio': dialogAspectRatio,
          } as CSSProperties
        }>
        <Typography
          variant={TypographyVariant.BodySmall}
          tag={TypographyTag.div}
          className={classNames(styles.zoomHint, styles.zoomHintDesktop)}>
          Click or scroll to zoom
        </Typography>
        <Typography
          variant={TypographyVariant.BodySmall}
          tag={TypographyTag.div}
          className={classNames(styles.zoomHint, styles.zoomHintMobile)}>
          Pinch to zoom
        </Typography>
        <button
          type='button'
          aria-label='Close gallery'
          onClick={onClose}
          className={styles.closeButton}>
          <IconX size={20} strokeWidth={1} color='var(--color-pdp-primary)' />
        </button>
        <Gallery
          slides={slides}
          selectedIndex={selectedIndex}
          onSelect={onSelect}
          showNavButtons={false}
          zoomable
          imagesOnly
          thumbnailsBelowMedia
        />
      </div>
    </div>,
    document.body
  )
}
